import { Question, QuestionCore, ModuleId, QuestionType } from '../types'
import { MODULES } from './modules'

// Import all questions with error handling
let METRIEK_QUESTIONS: QuestionCore[] = []
let GETALBEGRIP_QUESTIONS: QuestionCore[] = []
let TIJDGELD_QUESTIONS: QuestionCore[] = []
let SPELLINGWW_QUESTIONS: QuestionCore[] = []
let SPELLINGREGEL_QUESTIONS: QuestionCore[] = []
let TAALBEGRIP_QUESTIONS: QuestionCore[] = []

try {
  METRIEK_QUESTIONS = require('./questions/metriek').METRIEK_QUESTIONS || []
  GETALBEGRIP_QUESTIONS =
    require('./questions/getalbegrip').GETALBEGRIP_QUESTIONS || []
  TIJDGELD_QUESTIONS = require('./questions/tijdgeld').TIJDGELD_QUESTIONS || []
  SPELLINGWW_QUESTIONS =
    require('./questions/spellingww').SPELLINGWW_QUESTIONS || []
  SPELLINGREGEL_QUESTIONS =
    require('./questions/spellingregel').SPELLINGREGEL_QUESTIONS || []
  TAALBEGRIP_QUESTIONS =
    require('./questions/taalbegrip').TAALBEGRIP_QUESTIONS || []
} catch (error) {
  console.error('❌ Error loading question files:', error)
}

// Import all explanations with error handling
let METRIEK_EXPLANATIONS: any = {}
let GETALBEGRIP_EXPLANATIONS: any = {}
let TIJDGELD_EXPLANATIONS: any = {}
let SPELLINGWW_EXPLANATIONS: any = {}
let SPELLINGREGEL_EXPLANATIONS: any = {}
let TAALBEGRIP_EXPLANATIONS: any = {}

try {
  const explanations = require('./explanations')
  METRIEK_EXPLANATIONS = explanations.METRIEK_EXPLANATIONS || {}
  GETALBEGRIP_EXPLANATIONS = explanations.GETALBEGRIP_EXPLANATIONS || {}
  TIJDGELD_EXPLANATIONS = explanations.TIJDGELD_EXPLANATIONS || {}
  SPELLINGWW_EXPLANATIONS = explanations.SPELLINGWW_EXPLANATIONS || {}
  SPELLINGREGEL_EXPLANATIONS = explanations.SPELLINGREGEL_EXPLANATIONS || {}
  TAALBEGRIP_EXPLANATIONS = explanations.TAALBEGRIP_EXPLANATIONS || {}
} catch (error) {
  console.error('❌ Error loading explanation files:', error)
}

// Combine all raw questions (before normalization)
const RAW_QUESTIONS: QuestionCore[] = [
  ...METRIEK_QUESTIONS,
  ...GETALBEGRIP_QUESTIONS,
  ...TIJDGELD_QUESTIONS,
  ...SPELLINGWW_QUESTIONS,
  ...SPELLINGREGEL_QUESTIONS,
  ...TAALBEGRIP_QUESTIONS,
]

/**
 * PHASE 1: Question Type Migration v1.1
 *
 * Normalizes questions to ensure backwards compatibility while preparing for CITO.
 * All questions without explicit questionType are treated as 'multiple_choice'.
 */
export function normalizeQuestion(question: QuestionCore): QuestionCore {
  return {
    ...question,
    questionType: question.questionType || 'multiple_choice',
  }
}

/**
 * CITO Eligibility Check
 *
 * Determines if a question can be used in CITO-style assessments.
 * Currently only 'multiple_choice' questions are eligible.
 *
 * Future: May also check citoDifficulty, citoDiscrimination, and familyId constraints.
 */
export function isCitoEligible(question: QuestionCore): boolean {
  const type = question.questionType || 'multiple_choice'

  // Only multiple_choice questions are CITO-eligible
  // 'standard' is treated as legacy multiple_choice
  return type === 'multiple_choice' || type === 'standard'
}

/**
 * Validation Helper (Phase 1 - warnings only)
 *
 * Checks for potential issues in question structure.
 * Returns warnings but does not block loading.
 */
export function validateQuestion(question: QuestionCore): string[] {
  const warnings: string[] = []

  // Warn if familyId exists without skill
  if (question.familyId && !question.skill) {
    warnings.push(`Question ${question.id} has familyId but no skill defined`)
  }

  // Warn if CITO fields are partially filled
  if (
    question.citoDifficulty !== undefined &&
    question.citoDiscrimination === undefined
  ) {
    warnings.push(
      `Question ${question.id} has citoDifficulty but no citoDiscrimination`,
    )
  }

  // Warn if anchor question lacks CITO metrics
  if (
    question.isAnchor &&
    (question.citoDifficulty === undefined ||
      question.citoDiscrimination === undefined)
  ) {
    warnings.push(
      `Anchor question ${question.id} should have CITO difficulty and discrimination values`,
    )
  }

  return warnings
}

// Combine all questions (normalized, without explanations) - for Cito
export const ALL_QUESTIONS_CORE: QuestionCore[] =
  RAW_QUESTIONS.map(normalizeQuestion)

// NEW: CITO-eligible questions subset
export const CITO_ELIGIBLE_QUESTIONS: QuestionCore[] =
  ALL_QUESTIONS_CORE.filter(isCitoEligible)

// Combine all explanations
const ALL_EXPLANATIONS = {
  ...METRIEK_EXPLANATIONS,
  ...GETALBEGRIP_EXPLANATIONS,
  ...TIJDGELD_EXPLANATIONS,
  ...SPELLINGWW_EXPLANATIONS,
  ...SPELLINGREGEL_EXPLANATIONS,
  ...TAALBEGRIP_EXPLANATIONS,
}

/**
 * Get a single question with its explanation
 */
export function getQuestionWithExplanation(
  questionId: string,
): Question | null {
  try {
    const core = ALL_QUESTIONS_CORE.find((q) => q.id === questionId)
    if (!core) {
      console.error(`❌ QUESTION ERROR: Question not found: ${questionId}`)
      return null
    }

    const explanation = ALL_EXPLANATIONS[questionId]
    if (!explanation) {
      console.warn(`⚠️ MISSING EXPLANATION: ${questionId} (${core.module})`)
      // Return question without explanation if not found
      return {
        ...core,
        explanation: 'Uitleg wordt binnenkort toegevoegd.',
        distractorExplanations: {},
      }
    }

    // Validate explanation structure
    if (!explanation.explanation) {
      console.warn(
        `⚠️ INCOMPLETE EXPLANATION: ${questionId} - missing main explanation`,
      )
    }

    if (
      !explanation.distractorExplanations ||
      Object.keys(explanation.distractorExplanations).length === 0
    ) {
      console.warn(
        `⚠️ INCOMPLETE EXPLANATION: ${questionId} - missing distractor explanations`,
      )
    }

    // Validate visual aid if present
    if (explanation.visualAid) {
      if (!explanation.visualAid.type) {
        console.error(`❌ VISUAL AID ERROR: ${questionId} - missing type`)
      }
      if (!explanation.visualAid.data) {
        console.error(`❌ VISUAL AID ERROR: ${questionId} - missing data`)
      }
    }

    return {
      ...core,
      explanation: explanation.explanation,
      distractorExplanations: explanation.distractorExplanations,
      visualAid: explanation.visualAid,
    }
  } catch (error) {
    console.error('❌ Error in getQuestionWithExplanation:', error, questionId)
    return null
  }
}

/**
 * Get all questions for a module (with explanations)
 * Used by: PracticeSession, normal practice mode
 */
export function getQuestionsByModule(moduleId: ModuleId): Question[] {
  try {
    const questions = ALL_QUESTIONS_CORE.filter((q) => q.module === moduleId)
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)

    if (questions.length === 0) {
      console.warn(`⚠️ No questions found for module: ${moduleId}`)
    }

    return questions
  } catch (error) {
    console.error('❌ Error in getQuestionsByModule:', error, moduleId)
    return []
  }
}

/**
 * UPDATED: Get questions for CITO (with intelligent selection)
 *
 * CITO Selection Algorithm v1.1:
 * - Only CITO-eligible questions (multiple_choice)
 * - Max 1 question per familyId (prevents repeated variants)
 * - Balanced across skills (proportional representation)
 * - Balanced difficulty spread (mix of levels 1, 2, 3)
 * - Randomized within constraints
 *
 * Used by: CitoSimulator
 */
export function getCitoQuestions(count: number = 30): QuestionCore[] {
  try {
    // 1. Filter CITO-eligible questions only
    const eligible = CITO_ELIGIBLE_QUESTIONS.filter(isCitoEligible)

    if (eligible.length === 0) {
      console.error('❌ CITO: No eligible questions found')
      return []
    }

    // 2. Group by skill for balanced coverage
    const bySkill = new Map<string, QuestionCore[]>()
    eligible.forEach((q) => {
      const skill = q.skill || 'unknown'
      if (!bySkill.has(skill)) {
        bySkill.set(skill, [])
      }
      bySkill.get(skill)!.push(q)
    })

    // 3. Within each skill, group by familyId and pick max 1 per family
    const selectedBySkill = new Map<string, QuestionCore[]>()

    bySkill.forEach((questions, skill) => {
      const byFamily = new Map<string, QuestionCore[]>()

      questions.forEach((q) => {
        const familyId = q.familyId || q.id // Fallback to question ID if no familyId
        if (!byFamily.has(familyId)) {
          byFamily.set(familyId, [])
        }
        byFamily.get(familyId)!.push(q)
      })

      // Pick one random question from each family
      const skillQuestions: QuestionCore[] = []
      byFamily.forEach((familyQuestions) => {
        const randomIndex = Math.floor(Math.random() * familyQuestions.length)
        skillQuestions.push(familyQuestions[randomIndex])
      })

      selectedBySkill.set(skill, skillQuestions)
    })

    // 4. Calculate questions per skill (proportional to available questions)
    const totalAvailable = Array.from(selectedBySkill.values()).reduce(
      (sum, questions) => sum + questions.length,
      0,
    )

    const questionsPerSkill = new Map<string, number>()
    selectedBySkill.forEach((questions, skill) => {
      const proportion = questions.length / totalAvailable
      const allocated = Math.max(1, Math.round(count * proportion))
      questionsPerSkill.set(skill, allocated)
    })

    // 5. Select questions from each skill with difficulty balance
    const selected: QuestionCore[] = []

    selectedBySkill.forEach((questions, skill) => {
      const targetCount = questionsPerSkill.get(skill) || 0

      // Group by difficulty within skill
      const byDifficulty = {
        1: questions.filter((q) => q.difficulty === 1),
        2: questions.filter((q) => q.difficulty === 2),
        3: questions.filter((q) => q.difficulty === 3),
      }

      // Aim for difficulty distribution: 40% easy, 40% medium, 20% hard
      const easyCount = Math.round(targetCount * 0.4)
      const mediumCount = Math.round(targetCount * 0.4)
      const hardCount = targetCount - easyCount - mediumCount

      // Shuffle each difficulty level
      const shuffled1 = byDifficulty[1].sort(() => Math.random() - 0.5)
      const shuffled2 = byDifficulty[2].sort(() => Math.random() - 0.5)
      const shuffled3 = byDifficulty[3].sort(() => Math.random() - 0.5)

      // Take from each difficulty level
      selected.push(...shuffled1.slice(0, easyCount))
      selected.push(...shuffled2.slice(0, mediumCount))
      selected.push(...shuffled3.slice(0, hardCount))

      // If we don't have enough questions at target difficulties, fill from available
      const remaining =
        targetCount -
        (Math.min(easyCount, shuffled1.length) +
          Math.min(mediumCount, shuffled2.length) +
          Math.min(hardCount, shuffled3.length))

      if (remaining > 0) {
        const allShuffled = questions
          .filter((q) => !selected.includes(q))
          .sort(() => Math.random() - 0.5)
        selected.push(...allShuffled.slice(0, remaining))
      }
    })

    // 6. Final shuffle and limit to exact count
    const finalSelection = selected
      .sort(() => Math.random() - 0.5)
      .slice(0, count)

    // 7. Log selection statistics (development only)
    if (process.env.NODE_ENV === 'development') {
      console.group('🎯 CITO Question Selection')
      console.log(`Requested: ${count} questions`)
      console.log(`Selected: ${finalSelection.length} questions`)

      // Skill distribution
      const skillDist = new Map<string, number>()
      finalSelection.forEach((q) => {
        const skill = q.skill || 'unknown'
        skillDist.set(skill, (skillDist.get(skill) || 0) + 1)
      })
      console.log('Skill distribution:', Object.fromEntries(skillDist))

      // Difficulty distribution
      const diffDist = { 1: 0, 2: 0, 3: 0 }
      finalSelection.forEach((q) => {
        diffDist[q.difficulty as 1 | 2 | 3]++
      })
      console.log('Difficulty distribution:', diffDist)

      // Family uniqueness check
      const familyIds = new Set(finalSelection.map((q) => q.familyId))
      console.log(
        `Unique families: ${familyIds.size} (should equal ${finalSelection.length})`,
      )

      console.groupEnd()
    }

    if (finalSelection.length < count) {
      console.warn(
        `⚠️ CITO: Requested ${count} questions, but only ${finalSelection.length} available after filtering`,
      )
    }

    return finalSelection
  } catch (error) {
    console.error('❌ Error in getCitoQuestions:', error)
    return []
  }
}

/**
 * NEW: Get CITO questions by skill (for targeted practice)
 *
 * Selects questions from a specific skill with same CITO constraints
 */
export function getCitoQuestionsBySkill(
  skill: string,
  count: number = 10,
): QuestionCore[] {
  try {
    const eligible = CITO_ELIGIBLE_QUESTIONS.filter(
      (q) => isCitoEligible(q) && q.skill === skill,
    )

    if (eligible.length === 0) {
      console.warn(`⚠️ CITO: No eligible questions found for skill: ${skill}`)
      return []
    }

    // Group by familyId
    const byFamily = new Map<string, QuestionCore[]>()
    eligible.forEach((q) => {
      const familyId = q.familyId || q.id
      if (!byFamily.has(familyId)) {
        byFamily.set(familyId, [])
      }
      byFamily.get(familyId)!.push(q)
    })

    // Pick one from each family
    const uniqueQuestions: QuestionCore[] = []
    byFamily.forEach((familyQuestions) => {
      const randomIndex = Math.floor(Math.random() * familyQuestions.length)
      uniqueQuestions.push(familyQuestions[randomIndex])
    })

    // Balance difficulty
    const byDifficulty = {
      1: uniqueQuestions.filter((q) => q.difficulty === 1),
      2: uniqueQuestions.filter((q) => q.difficulty === 2),
      3: uniqueQuestions.filter((q) => q.difficulty === 3),
    }

    const easyCount = Math.round(count * 0.4)
    const mediumCount = Math.round(count * 0.4)
    const hardCount = count - easyCount - mediumCount

    const selected: QuestionCore[] = []
    selected.push(
      ...byDifficulty[1].sort(() => Math.random() - 0.5).slice(0, easyCount),
    )
    selected.push(
      ...byDifficulty[2].sort(() => Math.random() - 0.5).slice(0, mediumCount),
    )
    selected.push(
      ...byDifficulty[3].sort(() => Math.random() - 0.5).slice(0, hardCount),
    )

    // Fill remaining if needed
    const remaining = count - selected.length
    if (remaining > 0) {
      const availableQuestions = uniqueQuestions
        .filter((q) => !selected.includes(q))
        .sort(() => Math.random() - 0.5)
      selected.push(...availableQuestions.slice(0, remaining))
    }

    return selected.sort(() => Math.random() - 0.5).slice(0, count)
  } catch (error) {
    console.error('❌ Error in getCitoQuestionsBySkill:', error)
    return []
  }
}

/**
 * NEW: Get skill-based performance summary
 *
 * Analyzes available CITO questions by skill and subSkill
 */
export function getCitoSkillSummary(): {
  totalQuestions: number
  bySkill: Map<
    string,
    {
      total: number
      bySubSkill: Map<string, number>
      byDifficulty: { 1: number; 2: number; 3: number }
    }
  >
} {
  const bySkill = new Map<
    string,
    {
      total: number
      bySubSkill: Map<string, number>
      byDifficulty: { 1: number; 2: number; 3: number }
    }
  >()

  CITO_ELIGIBLE_QUESTIONS.forEach((q) => {
    const skill = q.skill || 'unknown'

    if (!bySkill.has(skill)) {
      bySkill.set(skill, {
        total: 0,
        bySubSkill: new Map(),
        byDifficulty: { 1: 0, 2: 0, 3: 0 },
      })
    }

    const skillData = bySkill.get(skill)!
    skillData.total++
    skillData.byDifficulty[q.difficulty as 1 | 2 | 3]++

    if (q.subSkill) {
      const subSkillCount = skillData.bySubSkill.get(q.subSkill) || 0
      skillData.bySubSkill.set(q.subSkill, subSkillCount + 1)
    }
  })

  return {
    totalQuestions: CITO_ELIGIBLE_QUESTIONS.length,
    bySkill,
  }
}

/**
 * Get hard questions (difficulty 3) across all modules
 * Used by: Superkracht Training (FocusPanel)
 */
export function getHardQuestions(): Question[] {
  try {
    return ALL_QUESTIONS_CORE.filter((q) => q.difficulty === 3)
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    console.error('❌ Error in getHardQuestions:', error)
    return []
  }
}

/**
 * Get hard questions for a specific module
 * Used by: Module-specific Superkracht Training
 */
export function getHardQuestionsByModule(moduleId: ModuleId): Question[] {
  try {
    return ALL_QUESTIONS_CORE.filter(
      (q) => q.module === moduleId && q.difficulty === 3,
    )
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    console.error('❌ Error in getHardQuestionsByModule:', error)
    return []
  }
}

/**
 * Get all questions (with explanations) - backwards compatible
 * Used by: Legacy code that expects full questions
 */
export function getAllQuestions(): Question[] {
  try {
    const questions = ALL_QUESTIONS_CORE.map((q) =>
      getQuestionWithExplanation(q.id),
    ).filter((q): q is Question => q !== null)

    // Log statistics
    const totalQuestions = ALL_QUESTIONS_CORE.length
    const questionsWithExplanations = questions.filter(
      (q) =>
        q.explanation &&
        q.explanation !== 'Uitleg wordt binnenkort toegevoegd.',
    ).length
    const missingExplanations = totalQuestions - questionsWithExplanations

    console.log(`📊 Question Statistics:`)
    console.log(`   Total questions: ${totalQuestions}`)
    console.log(`   With explanations: ${questionsWithExplanations}`)
    console.log(`   Missing explanations: ${missingExplanations}`)

    if (missingExplanations > 0) {
      console.warn(
        `⚠️ ${missingExplanations} questions are missing explanations`,
      )
    }

    return questions
  } catch (error) {
    console.error('❌ Error in getAllQuestions:', error)
    return []
  }
}

/**
 * NIEUW: Get demo questions (for testing new features)
 * Used by: Development and feature demonstration
 */
export function getDemoQuestions(): Question[] {
  // Return empty array - demo questions not implemented yet
  return []
}

/**
 * NIEUW: Get questions by type (for special rendering)
 * Used by: PracticeSession to render special question types
 */
export function getQuestionsByType(type: string): Question[] {
  try {
    return ALL_QUESTIONS_CORE.filter((q) => q.questionType === type)
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    console.error('❌ Error in getQuestionsByType:', error)
    return []
  }
}

/**
 * NIEUW: Validate all questions and explanations
 * Used by: Development to check data integrity
 */
export function validateQuestionsAndExplanations(): {
  valid: boolean
  errors: string[]
  warnings: string[]
} {
  const errors: string[] = []
  const warnings: string[] = []

  ALL_QUESTIONS_CORE.forEach((question) => {
    // Validate question structure
    if (!question.id) {
      errors.push(`Question missing ID`)
    }
    if (!question.module) {
      errors.push(`Question ${question.id} missing module`)
    }
    if (!question.question) {
      errors.push(`Question ${question.id} missing question text`)
    }
    if (!question.options || question.options.length === 0) {
      errors.push(`Question ${question.id} missing options`)
    }
    if (
      question.correctAnswer === undefined ||
      question.correctAnswer === null
    ) {
      errors.push(`Question ${question.id} missing correctAnswer`)
    }

    // Check for explanation
    const explanation = ALL_EXPLANATIONS[question.id]
    if (!explanation) {
      warnings.push(`Question ${question.id} missing explanation`)
    } else {
      if (!explanation.explanation) {
        warnings.push(`Question ${question.id} missing main explanation text`)
      }
      if (
        !explanation.distractorExplanations ||
        Object.keys(explanation.distractorExplanations).length === 0
      ) {
        warnings.push(`Question ${question.id} missing distractor explanations`)
      }
    }
  })

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

// Export modules for convenience
export { MODULES }

// UPDATED: Add validation logging (development only)
if (process.env.NODE_ENV === 'development') {
  const allWarnings = ALL_QUESTIONS_CORE.flatMap(validateQuestion)
  if (allWarnings.length > 0) {
    console.group('📋 Question Validation Warnings (Phase 1)')
    allWarnings.forEach((warning) => console.warn(warning))
    console.groupEnd()
  }

  console.log(
    `✅ Loaded ${ALL_QUESTIONS_CORE.length} questions (${CITO_ELIGIBLE_QUESTIONS.length} CITO-eligible)`,
  )
}
